import {
	CardType,
	type AnalysisCardConfig,
	type Analysis,
} from '../models/card-model';
import {
	analysisKey,
	analysisKeysMatch,
	analysisRangeKey,
	hasPersistedAnalysisId,
	normalizeAnalyses,
} from './analysis.util';

function wsItemMatchesConfig(
	wsItem: Analysis,
	configItem: Analysis
): boolean {
	if (analysisKeysMatch(wsItem, configItem)) {
		return true;
	}
	const configRange = analysisRangeKey(configItem);
	const wsRange = analysisRangeKey(wsItem);
	return (
		configRange != null &&
		wsRange != null &&
		configRange === wsRange &&
		!hasPersistedAnalysisId(wsItem)
	);
}

function wsItemAlreadyMerged(
	wsItem: Analysis,
	merged: Analysis[]
): boolean {
	return merged.some(item => wsItemMatchesConfig(wsItem, item));
}

function analysisHasDisplayableCopy(item: Analysis): boolean {
	return (
		(item.headline?.trim().length ?? 0) > 0 ||
		(item.content?.trim().length ?? 0) > 0
	);
}

/**
 * Config wins when it has headline/content (editor edits). Empty create-flow
 * drafts must not overwrite WebSocket / DataService copy.
 */
function mergeConfigAndWsAnalysis(
	configItem: Analysis,
	fromWs: Analysis
): Analysis {
	if (!analysisHasDisplayableCopy(configItem)) {
		return {
			...configItem,
			...fromWs,
			id: hasPersistedAnalysisId(configItem) ? configItem.id : fromWs.id,
			label: configItem.label ?? fromWs.label,
		};
	}
	return { ...fromWs, ...configItem };
}

/**
 * Resolves analyses for layout/editor.
 *
 * - **Connected card / grid:** `getItem.card` is shell-only (no analyses). When
 *   WebSocket has rows, those are the source of truth. Empty config drafts do
 *   not overwrite WS headline/content.
 * - **Editor / persist baseline:** `config.analyses` is hydrated (open + inline
 *   `updateAnalysis`). Merge by persisted `id`, or by period timestamps when WS rows
 *   only carry a {@link Analysis.runtimeId}.
 */
export function resolveAnalyses(
	config: AnalysisCardConfig,
	wsPeriods: Analysis[] | undefined
): Analysis[] {
	const configItems = normalizeAnalyses(config.analyses ?? []);
	const wsItems = wsPeriods?.length ? normalizeAnalyses(wsPeriods) : [];

	if (wsItems.length === 0) {
		return configItems;
	}

	if (configItems.length === 0) {
		return wsItems;
	}

	const wsById = new Map(
		wsItems
			.filter(item => hasPersistedAnalysisId(item))
			.map(item => [item.id.trim(), item] as const)
	);
	const wsByRange = new Map(
		wsItems
			.filter(item => !hasPersistedAnalysisId(item))
			.flatMap(item => {
				const rangeKey = analysisRangeKey(item);
				return rangeKey ? [[rangeKey, item] as const] : [];
			})
	);

	const merged: Analysis[] = configItems.map(item => {
		const rangeKey = analysisRangeKey(item);
		const fromWs =
			wsById.get(item.id.trim()) ??
			(rangeKey ? wsByRange.get(rangeKey) : undefined);
		if (!fromWs) {
			return item;
		}
		const mergedItem: Analysis = mergeConfigAndWsAnalysis(item, fromWs);
		if (hasPersistedAnalysisId(mergedItem)) {
			const { runtimeId: _runtimeId, ...withoutRuntimeId } = mergedItem;
			return withoutRuntimeId;
		}
		return mergedItem;
	});

	for (const wsItem of wsItems) {
		if (!wsItemAlreadyMerged(wsItem, merged)) {
			merged.push(wsItem);
		}
	}

	return normalizeAnalyses(merged);
}

/** Minimal card shell for merge when only partial config is available. */
export function resolveAnalysesForCard(
	cardId: string,
	configPeriods: Analysis[] | undefined,
	wsPeriods: Analysis[] | undefined
): Analysis[] {
	return resolveAnalyses(
		{
			cardId,
			cardType: CardType.Analysis,
			analyses: configPeriods ?? [],
		},
		wsPeriods
	);
}
